import { useCallback, useEffect, useRef, useState } from "react";

import { getReportStrategyPolicyVersionPineScript } from "../../../services/reportsApi";

import {
  WORKSPACE_POLICY_PINE_PREVIEW_HIDE,
  WORKSPACE_POLICY_PINE_PREVIEW_OPEN,
  type WorkspacePolicyPinePreviewOpenDetail,
} from "./workspacePolicyPinePreviewEvents";

export type WorkspacePolicyPinePreviewState = {
  open: boolean;
  strategyId: string | null;
  policyVersionId: string | null;
  title: string;
  loading: boolean;
  error: string | null;
  code: string;
  hide: () => void;
};

type PreviewRequest = {
  strategyId: string;
  policyVersionId: string;
  title: string;
  policyRowVersion: string | null;
};

const EMPTY: Omit<WorkspacePolicyPinePreviewState, "hide"> = {
  open: false,
  strategyId: null,
  policyVersionId: null,
  title: "Policy Pinescript",
  loading: false,
  error: null,
  code: "",
};

export function useWorkspacePolicyPinePreview(): WorkspacePolicyPinePreviewState {
  const [state, setState] = useState(EMPTY);
  const requestRef = useRef<PreviewRequest | null>(null);

  const hide = useCallback(() => {
    requestRef.current = null;
    setState(EMPTY);
  }, []);

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<WorkspacePolicyPinePreviewOpenDetail>).detail;
      if (!detail?.strategyId || !detail?.policyVersionId) return;
      const request: PreviewRequest = {
        strategyId: detail.strategyId,
        policyVersionId: detail.policyVersionId,
        title: detail.title.trim() || "Policy Pinescript",
        policyRowVersion:
          typeof detail.policyRowVersion === "string" && detail.policyRowVersion.trim()
            ? detail.policyRowVersion.trim()
            : null,
      };
      requestRef.current = request;
      setState({
        open: true,
        strategyId: request.strategyId,
        policyVersionId: request.policyVersionId,
        title: request.title,
        loading: true,
        error: null,
        code: "",
      });
    };

    const onHide = () => {
      requestRef.current = null;
      setState(EMPTY);
    };

    window.addEventListener(WORKSPACE_POLICY_PINE_PREVIEW_OPEN, onOpen);
    window.addEventListener(WORKSPACE_POLICY_PINE_PREVIEW_HIDE, onHide);
    return () => {
      window.removeEventListener(WORKSPACE_POLICY_PINE_PREVIEW_OPEN, onOpen);
      window.removeEventListener(WORKSPACE_POLICY_PINE_PREVIEW_HIDE, onHide);
    };
  }, []);

  useEffect(() => {
    if (!state.open || !state.loading) return;
    const request = requestRef.current;
    if (!request) return;

    let cancelled = false;
    void (async () => {
      try {
        const resp = await getReportStrategyPolicyVersionPineScript(
          request.strategyId,
          request.policyVersionId,
          {
            ...(request.policyRowVersion != null
              ? { policy_row_version: request.policyRowVersion }
              : {}),
            label_style: "descriptive",
          }
        );
        if (cancelled || requestRef.current !== request) return;
        setState({
          open: true,
          strategyId: request.strategyId,
          policyVersionId: request.policyVersionId,
          title: request.title,
          loading: false,
          error: null,
          code: typeof resp?.pinescript === "string" ? resp.pinescript : "",
        });
      } catch (err) {
        if (cancelled || requestRef.current !== request) return;
        setState({
          open: true,
          strategyId: request.strategyId,
          policyVersionId: request.policyVersionId,
          title: request.title,
          loading: false,
          error: err instanceof Error ? err.message : "Failed to load Pinescript.",
          code: "",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state.loading, state.open, state.policyVersionId, state.strategyId]);

  return { ...state, hide };
}
